<template>
  <div class="app-main-content">
    <!-- 列表视图 -->
    <div v-show="showTab === 'list'" class="details-view">
      <!-- 顶部搜索栏和操作按钮 -->
      <div class="search-bar">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item>
            <span class="search-label">{{ $t('CargoMsg.Cargo_FormName') }}：</span>
            <el-input
              v-model="searchForm.keyword"
              :placeholder="$t('CargoMsg.Cargo_QuickSearchCustomPage')"
              class="search-input"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.applicationScene"
              :placeholder="$t('CargoMsg.Cargo_ApplicationScene')"
              clearable
            >
              <el-option
                v-for="item in applicationSceneOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.businessType"
              :placeholder="$t('CargoMsg.Cargo_BusinessType')"
              clearable
            >
              <el-option
                v-for="item in businessTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.enabled"
              :placeholder="$t('CargoMsg.Cargo_Status')"
              clearable
            >
              <el-option
                v-for="item in enableStatusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <div style="padding-left: 10px">
            <el-button type="primary" @click="handleSearch">
              {{ $t('CargoMsg.Cargo_Query') }}
            </el-button>
            <el-button type="primary" @click="handleAdd">
              {{ $t('CargoMsg.Cargo_Add') }}
            </el-button>
            <el-button type="primary" @click="handleCopy">
              {{ $t('CargoMsg.Cargo_Copy') }}
            </el-button>
            <el-button type="danger" @click="handleDelete">
              {{ $t('CargoMsg.Cargo_Delete') }}
            </el-button>
          </div>
        </el-form>
      </div>

      <!-- 中间列表视图 -->
      <div class="grid-view">
        <div class="main-content">
          <div class="section grid-wrapper">
            <cargo-ag-grid
              :key="gridKey"
              config-module="2025062001"
              :grid-options="gridOptions"
              :grid-params.sync="gridParams"
              :column-defs="columnDefs"
              :row-data="tableData"
              @gridReady="params=>{gridApi=params.api}"
            />
          </div>
        </div>
      </div>

      <!-- 底部分页 -->
      <div class="pagination-wrapper">
        <div class="pagination-container">
          <cargo-pagination
            config-module="2025062002"
            :total="pagination.total"
            :page.sync="pagination.pageNum"
            :limit.sync="pagination.pageSize"
            @pagination="handlePagination"
          />
        </div>
      </div>
    </div>

    <!-- 详情视图 -->
    <ai-custom-page-detail
      v-if="showTab === 'detail'"
      ref="pageDetail"
      :form-config="formConfig"
      :form-uuid="formUuid"
      @goBack="handleBack"
      @config="handleConfig"
    />

    <!-- 表单配置对话框 -->
    <ai-custom-page-form-config
      :visible.sync="formConfigVisible"
      :form-config="formConfig"
      :is-create-mode="isCreateMode"
      @confirm="handleFormConfigConfirm"
      @cancel="handleFormConfigCancel"
    />

    <!-- 预览对话框 -->
    <el-dialog
      :visible.sync="previewDialog.visible"
      :title="$t('CargoMsg.Cargo_Preview')"
      :fullscreen="false"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="preview-dialog"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      width="70%"
      top="20px"
      class="preview-dialog-wrapper"
    >
      <ai-custom-page-data-view
        v-if="previewDialog.visible"
        :page-parts="previewFormConfig.pageParts"
        :form-config="previewFormConfig"
        :form-data="safeParseJson(previewFormConfig.formDataJson, {})"
        :debug="true"
        :readonly="true"
        @close="previewDialog.visible = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import OperationRender from '@/components/OperationRender'
import AiCustomPageDetail from './components/aiCustomPage/aiCustomPageDetail.vue'
import AiCustomPageFormConfig from './components/aiCustomPage/aiCustomPageFormConfig.vue'
import AiCustomPageDataView from './components/aiCustomPage/aiCustomPageDataView.vue'
import { APPLICATION_SCENE, BUSINESS_TYPE, ENABLED, PUBLISHED } from '@/class/cargo/model/parameters/AiCustomPageConstants'
import {
  queryFormsByCondition,
  queryFormByUuid,
  enableFormByUuid,
  disableFormByUuid,
  deleteFormsByUuids,
  copyFormByUuid,
  saveForm
} from '@/api/sysManage/aiCustomPage'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import ColumnUtils from '@/utils/ColumnUtils'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { getCacheListByKey } from '@/utils/basicDataCache'
import { getLoading4Cxt } from '@/utils/utilBaseElement'

export default {
  name: 'AICustomPage',
  components: {
    AiCustomPageDetail,
    AiCustomPageFormConfig,
    AiCustomPageDataView
  },
  data() {
    return {
      showTab: 'list', // 控制当前显示的是列表还是详情视图
      formConfigVisible: false,
      formConfig: {
        uuid: null,
        name: '',
        applicationScene: APPLICATION_SCENE.BUSINESS_SINGLE.value,
        businessType: BUSINESS_TYPE.SEA_EXPORT.value,
        enabled: ENABLED.ENABLED.value,
        published: PUBLISHED.DRAFT.value,
        inputApi: '',
        outputApi: '',
        pageParts: [],
        formData: {}
      },
      formUuid: null, // 用于跟踪是创建还是编辑模式
      isCreateMode: false, // 添加创建模式状态
      searchForm: {
        keyword: '', // 表单名称
        applicationScene: '', // 应用场景
        businessType: '', // 业务类型
        enabled: '' // 启用状态
      },
      originalTableData: [],
      applicationSceneOptions: [
        { value: APPLICATION_SCENE.BUSINESS_SINGLE.value, label: this.$t(APPLICATION_SCENE.BUSINESS_SINGLE.key) },
        { value: APPLICATION_SCENE.BUSINESS_BATCH.value, label: this.$t(APPLICATION_SCENE.BUSINESS_BATCH.key) },
        { value: APPLICATION_SCENE.BPM.value, label: 'BPM' },
        { value: APPLICATION_SCENE.OCR.value, label: 'OCR' },
        { value: APPLICATION_SCENE.API.value, label: this.$t(APPLICATION_SCENE.API.key) }
      ],
      businessTypeOptions: [
        { value: BUSINESS_TYPE.SEA_EXPORT.value, label: this.$t(BUSINESS_TYPE.SEA_EXPORT.key) },
        { value: BUSINESS_TYPE.AIR_EXPORT.value, label: this.$t(BUSINESS_TYPE.AIR_EXPORT.key) }
      ],
      enableStatusOptions: [
        { value: ENABLED.ENABLED.value, label: this.$t(ENABLED.ENABLED.key) },
        { value: ENABLED.DISABLED.value, label: this.$t(ENABLED.DISABLED.key) }
      ],
      publishStatusOptions: [
        { value: PUBLISHED.DRAFT.value, label: this.$t(PUBLISHED.DRAFT.key) },
        { value: PUBLISHED.PUBLISHED.value, label: this.$t(PUBLISHED.PUBLISHED.key) }
      ],
      tableData: [],
      pagination: {
        pageNum: 1,
        pageSize: null,
        total: 0
      },
      selectedRows: [],
      gridApi: null,
      gridParams: {},
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        defaultColDef: {
          resizable: true,
          filter: true
        },
        pagination: false,
        paginationPageSize: 10,
        onRowClicked: event => { this.selectedRow = event.data },
        onRowDoubleClicked: event => { this.handleEdit(event.data) }
      },
      columnDefs: [
        {
          headerName: '', width: 55, checkboxSelection: true,
          sortable: false, suppressMenu: true, suppressSizeToFit: true, resizable: false,
          headerCheckboxSelection: true
        },
        {
          headerName: this.$t('CargoMsg.Cargo_FormId'),
          field: 'uuid'
        },
        {
          headerName: this.$t('CargoMsg.Cargo_FormName'),
          field: 'name'
        },
        {
          headerName: this.$t('CargoMsg.Cargo_ApplicationScene'),
          field: 'applicationScene',
          valueFormatter: params => {
            const scene = Object.values(APPLICATION_SCENE).find(s => s.value === params.value)
            return scene ? this.$t(scene.key) : this.$t('CargoMsg.Cargo_Unknown')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_BusinessType'),
          field: 'businessType',
          valueFormatter: params => {
            const type = Object.values(BUSINESS_TYPE).find(t => t.value === params.value)
            return type ? this.$t(type.key) : this.$t('CargoMsg.Cargo_Unknown')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_EnableStatus'),
          field: 'enabled',
          valueFormatter: params => {
            const status = Object.values(ENABLED).find(s => s.value === params.value)
            return status ? this.$t(status.key) : this.$t('CargoMsg.Cargo_Unknown')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_PublishStatus'),
          field: 'published',
          valueFormatter: params => {
            const status = Object.values(PUBLISHED).find(s => s.value === params.value)
            return status ? this.$t(status.key) : this.$t('CargoMsg.Cargo_Unknown')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Operator'),
          field: 'modifiedBy',
          cellRenderer: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_OperateTime'),
          field: 'dateModified'
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Company'),
          field: 'branchId',
          valueFormatter: params => {
            const office = getCacheListByKey(basicCacheKeys.basic_data_Office).find(o => o.officeId === params.value)
            if (!office) return this.$t('CargoMsg.Cargo_Unknown')
            switch (this.$store.getters.language) {
              case 'zh':
                return office.chineseName
              default:
                return office.englishName
            }
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Remarks'),
          field: 'remarks'
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Operation'),
          field: 'operation',
          width: 300,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: OperationRender,
          cellRendererParams: {
            operations: [
              {
                title: this.$t('CargoMsg.Cargo_Edit'),
                type: 'primary',
                cssStyle: 'text-decoration: underline;',
                onClick: (params) => {
                  this.handleEdit(params)
                },
                visible: () => true
              },
              {
                title: this.$t('CargoMsg.Cargo_Disable'),
                type: 'primary',
                cssStyle: 'text-decoration: underline;color: #f57174',
                onClick: (params) => {
                  this.handleToggleStatus(params)
                },
                visible: (params) => params.enabled === ENABLED.ENABLED.value
              },
              {
                title: this.$t('CargoMsg.Cargo_Enable'),
                type: 'primary',
                cssStyle: 'text-decoration: underline;',
                onClick: (params) => {
                  this.handleToggleStatus(params)
                },
                visible: (params) => params.enabled === ENABLED.DISABLED.value
              },
              {
                title: this.$t('CargoMsg.Cargo_Preview'),
                type: 'primary',
                cssStyle: 'text-decoration: underline;',
                onClick: (params) => {
                  this.handlePreview(params)
                },
                visible: () => true
              },
              {
                title: this.$t('CargoMsg.Cargo_Config'),
                type: 'primary',
                cssStyle: 'text-decoration: underline;',
                onClick: (params) => {
                  this.handleConfig(params)
                },
                visible: () => true
              }
            ]
          }
        }
      ],
      gridKey: 0, // 添加key用于强制重新渲染
      previewDialog: {
        visible: false
      },
      previewFormConfig: {},
      loading: null
    }
  },
  watch: {
    'pagination.pageNum'() {
      this.loadTableData()
    },
    'pagination.pageSize'() {
      this.loadTableData()
    }
  },
  methods: {
    async loadTableData() {
      try {
        // 构建请求参数，只包含非空值
        const requestData = {
          ...(this.searchForm.keyword && { name: this.searchForm.keyword }), // 表单名称，模糊查询
          ...(this.searchForm.applicationScene && { applicationScene: this.searchForm.applicationScene }), // 应用场景
          ...(this.searchForm.businessType && { businessType: this.searchForm.businessType }), // 业务类型
          ...(this.searchForm.enabled && { enabled: this.searchForm.enabled }), // 启用状态
          pageNum: this.pagination.pageNum, // 当前页码
          pageSize: this.pagination.pageSize, // 每页条数
          orderBy: 'DATE_MODIFIED', // 排序字段
          asc: false // 排序方向
        }

        console.log('查询表单列表，请求参数：', requestData)
        const response = await queryFormsByCondition(requestData)
        if (response && response.data) {
          this.tableData = response.data.list || []
          this.pagination.total = response.data.total || 0
          this.gridParams.pagination = {
            pageSize: this.pagination.pageSize,
            currentPage: this.pagination.pageNum
          }
          console.log('查询表单列表成功，数据：', this.tableData)
        }
      } catch (error) {
        console.error('查询表单列表失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_QueryFailed'))
      }
    },
    handleSearch() {
      // 重新加载数据
      this.loadTableData()
    },
    async handleAdd() {
      // 新增：打开表单配置对话框
      this.isCreateMode = true
      this.formUuid = null
      // 初始化formConfig
      this.formConfig = {
        uuid: null,
        name: '',
        applicationScene: APPLICATION_SCENE.BUSINESS_SINGLE.value,
        businessType: BUSINESS_TYPE.SEA_EXPORT.value,
        enabled: ENABLED.ENABLED.value,
        published: PUBLISHED.DRAFT.value,
        inputApi: '',
        outputApi: '',
        pageParts: [],
        formData: {}
      }
      this.formConfigVisible = true
    },
    handleCopy() {
      const rows = this.gridApi.getSelectedRows()
      if (rows.length === 0) {
        this.$message.warning(this.$t('CargoMsg.Cargo_CopyOne'))
        return
      }
      if (rows.length > 1) {
        this.$message.warning(this.$t('CargoMsg.Cargo_CopyOne'))
        return
      }

      this.$confirm(this.$t('CargoMsg.Cargo_CopyFormConfirm'), this.$t('Message.WarmReminder'), {
        confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
        cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
        type: 'info'
      }).then(() => {
        this.loading = getLoading4Cxt()
        copyFormByUuid({ uuid: rows[0].uuid }).then(response => {
          if (response && response.data) {
            this.$message.success(this.$t('CargoMsg.Cargo_CopySucceed'))
            this.loadTableData()
          }
        }).finally(() => {
          this.loading.close()
        })
      }).catch(() => {
        // 用户取消操作，不做任何处理
      })
    },
    async handleDelete() {
      const rows = this.gridApi.getSelectedRows()
      if (rows.length === 0) {
        this.$message.warning(this.$t('CargoMsg.Cargo_SelectDeleteTip4'))
        return
      }

      // 检查是否有启用的表单
      const enabledForms = rows.filter(row => row.enabled === ENABLED.ENABLED.value)
      if (enabledForms.length > 0) {
        this.$message.warning(this.$t('CargoMsg.Cargo_CannotDeleteEnabledForm'))
        return
      }

      try {
        await this.$confirm(this.$t('CargoMsg.Cargo_SelectDeleteTip5'), this.$t('Message.WarmReminder'), {
          dangerouslyUseHTMLString: true,
          confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
          cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
          type: 'warning'
        })

        // 获取所有选中行的UUID
        const uuids = rows.map(row => row.uuid)
        const response = await deleteFormsByUuids(uuids)
        if (response && response.data) {
          this.$message.success(this.$t('CargoMsg.Cargo_DeletedSuccessfully'))
          // 重新加载数据
          this.loadTableData()
        }
      } catch (error) {
        if (error === 'cancel') {
          return // 用户取消操作
        }
        console.error('删除表单失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_DeleteFailed'))
      }
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    async handleEdit(row) {
      try {
        this.loading = getLoading4Cxt()
        // 查询表单详情
        const response = await queryFormByUuid({ uuid: row.uuid })
        if (response && response.data) {
          this.formConfig = response.data
          this.formUuid = row.uuid
          this.isCreateMode = false
          this.showTab = 'detail'
        }
      } catch (error) {
        console.error('查询表单详情失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_QueryFailed'))
      } finally {
        this.loading.close()
      }
    },
    async handleToggleStatus(row) {
      try {
        // 如果是禁用操作，需要确认
        if (row.enabled === ENABLED.ENABLED.value) {
          await this.$confirm(this.$t('CargoMsg.Cargo_FormDisableWarning'), this.$t('Message.WarmReminder'), {
            confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
            cancelButtonText: this.$t('common.btn.cancel'),
            type: 'warning'
          })
          // 禁用表单
          const response = await disableFormByUuid({ uuid: row.uuid })
          if (response && response.data) {
            this.$message.success(this.$t('CargoMsg.Cargo_Disabled'))
            // 重新加载数据
            this.loadTableData()
          }
        } else {
          // 启用表单
          const response = await enableFormByUuid({ uuid: row.uuid })
          if (response && response.data) {
            this.$message.success(this.$t('CargoMsg.Cargo_Enabled'))
            // 重新加载数据
            this.loadTableData()
          }
        }
      } catch (error) {
        if (error === 'cancel') {
          return // 用户取消操作
        }
        console.error('切换表单状态失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_ToggleStatusFailed'))
      }
    },
    async handlePreview(row) {
      try {
        // 查询表单详情
        const response = await queryFormByUuid({ uuid: row.uuid })
        if (response && response.data) {
          this.previewFormConfig = response.data
          this.previewDialog.visible = true
        } else {
          this.$message.error(this.$t('CargoMsg.Cargo_GetFormConfigFailed'))
        }
      } catch (error) {
        console.error('获取表单详情失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_GetFormConfigFailed'))
      }
    },
    handleConfig(formConfig) {
      this.formConfig = {
        ...formConfig
      }
      console.log('配置按钮 - 表单配置:', this.formConfig)
      this.formConfigVisible = true
    },
    async handleFormConfigConfirm(formConfig) {
      if (this.isCreateMode) {
        // 如果是创建模式，将配置传递给详情页
        console.log('创建模式 - 新配置:', formConfig)
        this.formConfig = {
          ...formConfig,
          name: formConfig.name,
          branchId: formConfig.branchId,
          applicationScene: formConfig.applicationScene,
          businessType: formConfig.businessType,
          remarks: formConfig.remarks,
          inputApi: formConfig.inputApi,
          outputApi: formConfig.outputApi
        }
        console.log('创建模式 - 合并后配置:', this.formConfig)
        this.showTab = 'detail'
      } else {
        // 如果是编辑模式，更新并保存配置
        console.log('编辑模式 - 新配置:', formConfig)
        try {
          const response = await queryFormByUuid({ uuid: this.formConfig.uuid })
          if (response && response.data) {
            this.formConfig = {
              ...response.data,
              name: formConfig.name,
              branchId: formConfig.branchId,
              applicationScene: formConfig.applicationScene,
              businessType: formConfig.businessType,
              remarks: formConfig.remarks,
              inputApi: formConfig.inputApi,
              outputApi: formConfig.outputApi
            }
          }
        } catch (error) {
          console.error('获取表单配置失败:', error)
          this.$message.error(this.$t('CargoMsg.Cargo_GetFormConfigFailed'))
        }

        try {
          this.loading = getLoading4Cxt()
          // 调用保存API
          const response = await saveForm(this.formConfig)
          if (response && response.data) {
            // 获取保存后的完整表单配置
            const queryResponse = await queryFormByUuid({ uuid: response.data.uuid })
            if (queryResponse && queryResponse.data) {
              // 使用$set更新整个formConfig对象
              this.formConfig = queryResponse.data
            }
            this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
            // 刷新列表数据
            this.loadTableData()
          }
        } catch (error) {
          console.error('保存表单失败：', error)
          this.$message.error(this.$t('CargoMsg.Cargo_SaveFailed'))
        } finally {
          this.loading.close()
        }
      }
      this.formConfigVisible = false
      this.isCreateMode = false
    },
    handleFormConfigCancel() {
      this.formConfigVisible = false
      this.isCreateMode = false
    },
    handlePagination({ page, limit }) {
      this.loadTableData()
    },
    handleBack() {
      this.showTab = 'list'
      this.loadTableData()
    },
    // 安全解析JSON字符串
    safeParseJson(jsonStr, defaultValue = {}) {
      try {
        return jsonStr ? JSON.parse(jsonStr) : defaultValue
      } catch (error) {
        console.warn('[警告] 解析数据失败:', error)
        return {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-main-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.details-view {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.search-bar {
  padding: 10px 0;
  background-color: #fff;
  border-bottom: 1px solid #e6e6e6;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.search-label {
  margin-right: 8px;
  color: #606266;
}

.search-input {
  width: 200px;
}

.grid-view {
  margin: 10px 0;
  background-color: #fff;

  :deep(.cargo-ag-grid) {
    .ag-theme-balham {
      flex: none !important;
    }
  }
}

.pagination-wrapper {
  display: flex;
  flex-direction: row-reverse;
  margin-top: 5px;
  align-items: center;

  .pagination-container {
    flex: 1;
    padding: 0;
    text-align: left;
  }
}

.preview-dialog-wrapper {
  // z-index: 9999 !important;
}

.preview-dialog {
  margin: 0 !important;
  padding: 0 !important;
}

.preview-dialog .el-dialog__body {
  padding: 0 !important;
  height: 70vh;
}

.preview-dialog .el-dialog__header {
  display: none;
}

.preview-dialog .el-dialog__wrapper {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
